import { formatViewerSessionTime } from './viewerSessionTime';

import type { MemberTableRow } from './types';
import type { ViewerSessionZone } from './viewerSessionTime';
import type { MembersTableApiRow } from '@/store/api/userApi';

const formatDate = (value: string | null | undefined) => {
  if (!value) return null;

  const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(value);
  const date = dateOnlyMatch
    ? new Date(
        Number(dateOnlyMatch[1]),
        Number(dateOnlyMatch[2]) - 1,
        Number(dateOnlyMatch[3]),
      )
    : new Date(value);
  if (Number.isNaN(date.getTime())) return null;

  return new Intl.DateTimeFormat('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(date);
};

const formatSeatType = (seatType?: string | null): string | null => {
  if (!seatType) return null;
  if (seatType === 'executive') return 'Executive';
  if (seatType === 'standard_leadership') return 'Leadership';
  return seatType;
};

const formatInsightsStatus = (status?: string | null) => {
  if (!status) return 'Unsent';
  return status
    .split('_')
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(' ');
};

export const mapMembersTableRow = (
  row: MembersTableApiRow,
  viewerZone: ViewerSessionZone,
): MemberTableRow => {
  const upcomingSessions =
    row.sessions.upcomingSessions ??
    (row.sessions.nextSessionAt
      ? [
          {
            coach: undefined,
            startedAt: row.sessions.nextSessionAt,
            type: 'unknown' as const,
          },
        ]
      : []);

  return {
    adminNote: row.adminNote ?? null,
    coachName: row.coaches[0]?.name ?? null,
    coaches: row.coaches,
    cohortTags: row.cohortTags ?? [],
    company: row.company?.name ?? '—',
    companyId: row.company?.id ?? null,
    endDate: formatDate(row.contractEnd?.date),
    endDateStatus: row.contractEnd?.status ?? null,
    datePaused: formatDate(row.pause?.datePaused),
    returnDate: formatDate(row.pause?.returnDate),
    growthPlanStarted: Boolean(row.growthPlanStarted),
    insightsStatus: formatInsightsStatus(row.insightsStatus),
    joined: formatDate(row.joinedAt) ?? '—',
    seatType: formatSeatType(row.user.profile?.seatType),
    sessions: {
      chemCalls: row.sessions.chemCalls,
      completed: row.sessions.completed,
      upcoming: row.sessions.upcoming,
      upcomingSessions: upcomingSessions.map((session) => ({
        coach: session.coach,
        display: formatViewerSessionTime(session.startedAt, viewerZone, 'long'),
        startedAt: session.startedAt,
        type: session.type,
      })),
    },
    status: row.memberStatus,
    user: row.user,
  };
};
